import { useEffect, useState } from 'react';
import { Plus, X } from 'lucide-react';
import { getSDK } from '@/store/sdkStore';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

// ============================================================================
// lib/widgets/FilterListEditor.tsx — СТРУКТУРИРОВАННЫЙ редактор отборов
// виджета «Форма списка» (list). НЕ текст/JSON — строки «поле/условие/значение»
// с выбором поля из реквизитов объекта (SSoT — сервер). Спец-значение
// @current_user — id текущего пользователя («Мои заявки»). Хранится как
// обычный массив {field, condition, value} в props виджета (layout страницы).
// ============================================================================

export interface FilterRow {
  field: string;
  condition: string;
  value?: string;
}

export const FILTER_CONDITIONS: Array<{ value: string; label: string }> = [
  { value: '=', label: 'равно' },
  { value: '!=', label: 'не равно' },
  { value: '>', label: 'больше' },
  { value: '<', label: 'меньше' },
  { value: '>=', label: 'больше или равно' },
  { value: '<=', label: 'меньше или равно' },
  { value: 'like', label: 'содержит' },
  { value: 'is_null', label: 'не заполнено' },
  { value: 'is_not_null', label: 'заполнено' },
];

// Общие (системные) поля — всегда доступны для отбора (created_by — «Мои заявки»).
const SYSTEM_FIELDS: Array<{ code: string; label: string }> = [
  { code: 'created_by', label: 'Создал (текущий пользователь)' },
  { code: 'updated_by', label: 'Изменил' },
  { code: 'status_code', label: 'Статус' },
  { code: 'name', label: 'Наименование / Тема' },
  { code: 'number', label: 'Номер' },
  { code: 'is_marked', label: 'Пометка удаления' },
];

interface FieldMeta { code: string; label: string; dataType: string; refTable: string; }

export function FilterListEditor({ value, onChange, objectType, objectName }: {
  value?: FilterRow[] | string;
  onChange: (v: FilterRow[]) => void;
  objectType?: string;
  objectName?: string;
}) {
  const [fields, setFields] = useState<FieldMeta[]>([]);
  // Значения перечислений по коду перечисления: {код: {значение: синоним}} —
  // для отбора по полю-перечислению показываем СИНОНИМЫ (SSoT — сервер, getEnum).
  const [enumValues, setEnumValues] = useState<Record<string, Record<string, string>>>({});

  // Реквизиты объекта для выбора поля (SSoT — сервер).
  useEffect(() => {
    let cancelled = false;
    if (!objectType || !objectName) {
      setFields([]);
      setEnumValues({});
      return;
    }
    getSDK().metadata.listFields({ entityType: objectType, entityCode: objectName })
      .then(async (r: any) => {
        if (cancelled) return;
        const items: FieldMeta[] = ((r?.items || []) as any[]).map((f: any) => ({
          code: String(f.code || f.name || ''),
          label: String(f.synonym || f.name || f.code || ''),
          dataType: String(f.dataType || f.data_type || '').toLowerCase(),
          refTable: String(f.refTable || f.refCatalogCode || ''),
        }));
        setFields(items);
        // Значения перечислений (только коды перечислений полей этого объекта).
        const enums: Record<string, Record<string, string>> = {};
        const refs = Array.from(new Set<string>(
          items.filter((f) => f.dataType === 'enumeration' && f.refTable).map((f) => f.refTable),
        ));
        for (const ref of refs) {
          try {
            const er: any = await getSDK().metadata.getEnum({ code: ref });
            const m: Record<string, string> = {};
            for (const v of er?.values || []) m[String(v.value)] = String(v.synonym || v.name || v.value);
            enums[ref] = m;
          } catch { /* перечисление недоступно — останется текстовый ввод */ }
        }
        if (!cancelled) setEnumValues(enums);
      })
      .catch(() => { if (!cancelled) { setFields([]); setEnumValues({}); } });
    return () => { cancelled = true; };
  }, [objectType, objectName]);

  /** Варианты значений для поля (только перечисление с доступным справочником). */
  const optionsFor = (code: string): Record<string, string> | undefined => {
    const f = fields.find((x) => x.code === code);
    if (!f || f.dataType !== 'enumeration' || !f.refTable) return undefined;
    return enumValues[f.refTable];
  };

  // Нормализуем значение: массив или JSON-строка (легаси) → массив.
  const rows: FilterRow[] = (() => {
    if (Array.isArray(value)) return value as FilterRow[];
    if (typeof value === 'string' && value.trim()) {
      try { const p = JSON.parse(value); return Array.isArray(p) ? (p as FilterRow[]) : []; } catch { return []; }
    }
    return [];
  })();

  const setRow = (i: number, patch: Partial<FilterRow>) => {
    const next = rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r));
    onChange(next);
  };
  const addRow = () => onChange([...rows, { field: fields[0]?.code || 'created_by', condition: '=', value: fields[0]?.code === 'created_by' ? '@current_user' : '' }]);
  const removeRow = (i: number) => onChange(rows.filter((_, idx) => idx !== i));

  const needsValue = (c: string) => c !== 'is_null' && c !== 'is_not_null';

  return (
    <div className="space-y-1.5">
      {rows.length === 0 && (
        <div className="text-[10px] text-muted-foreground">Отборов нет — показываются все записи</div>
      )}
      {rows.map((r, i) => {
        const fieldList = [...SYSTEM_FIELDS, ...fields];
        const knownField = fieldList.find((f) => f.code === r.field);
        const isCustomField = !!r.field && !knownField;
        return (
          <div key={i} className="flex items-center gap-1 text-[10px]">
            <select
              className="h-7 min-w-0 flex-1 border rounded bg-background px-1 text-[10px]"
              value={isCustomField ? '__custom__' : (r.field || '')}
              onChange={(e) => {
                const v = e.target.value;
                if (v === '__custom__') {
                  setRow(i, { field: r.field || 'created_by', condition: r.condition || '=' });
                } else {
                  // Для created_by подставляем @current_user («Мои заявки») по умолчанию.
                  setRow(i, { field: v, value: v === 'created_by' && !r.value ? '@current_user' : r.value });
                }
              }}
            >
              <option value="">— поле —</option>
              {fieldList.map((f) => (
                <option key={f.code} value={f.code}>{f.code} · {f.label}</option>
              ))}
              <option value="__custom__">{isCustomField ? `✎ ${r.field}` : '✎ произвольное поле…'}</option>
            </select>
            {isCustomField && (
              <Input
                className="h-7 min-w-0 flex-1 text-[10px]"
                value={r.field}
                placeholder="имя поля"
                onChange={(e) => setRow(i, { field: e.target.value })}
              />
            )}
            <select
              className="h-7 w-24 border rounded bg-background px-1 text-[10px]"
              value={r.condition || '='}
              onChange={(e) => setRow(i, { condition: e.target.value })}
            >
              {FILTER_CONDITIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
            {needsValue(r.condition) && (() => {
              const opts = optionsFor(r.field);
              if (opts) {
                return (
                  <select
                    className="h-7 min-w-0 flex-1 border rounded bg-background px-1 text-[10px]"
                    value={r.value ?? ''}
                    onChange={(e) => setRow(i, { value: e.target.value })}
                  >
                    <option value="">— значение —</option>
                    {r.value && !opts[r.value] && <option value={r.value}>{r.value}</option>}
                    {Object.entries(opts).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                );
              }
              return (
                <Input
                  className="h-7 min-w-0 flex-1 text-[10px]"
                  value={r.value ?? ''}
                  placeholder={r.field === 'created_by' ? '@current_user' : 'значение'}
                  onChange={(e) => setRow(i, { value: e.target.value })}
                />
              );
            })()}
            <button className="p-0.5 text-muted-foreground hover:text-destructive" onClick={() => removeRow(i)} title="Убрать отбор">
              <X className="h-3 w-3" />
            </button>
          </div>
        );
      })}
      <div className="flex items-center justify-between gap-2">
        <Button size="sm" variant="outline" className="h-6 text-[10px] px-2" onClick={addRow}>
          <Plus className="h-3 w-3 mr-1" /> Отбор
        </Button>
        <span className="text-[9px] text-muted-foreground">@current_user = текущий пользователь</span>
      </div>
    </div>
  );
}

// ============================================================================
// ParamsEditor — ПАРАМЕТРЫ ВИДЖЕТА (name → value). Локальны для виджета,
// в отборах ссылаются как @param.<имя>. НЕ текст — структурированные строки.
// ============================================================================

export function ParamsEditor({ value, onChange }: {
  value?: Record<string, string>;
  onChange: (v: Record<string, string>) => void;
}) {
  const rows: Array<{ name: string; val: string }> = (() => {
    const src = value && typeof value === 'object' ? value : {};
    return Object.entries(src).map(([name, val]) => ({ name, val: String(val ?? '') }));
  })();

  const setRow = (i: number, patch: Partial<{ name: string; val: string }>) => {
    const next = rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r));
    const obj: Record<string, string> = {};
    for (const r of next) if (r.name.trim()) obj[r.name.trim()] = r.val;
    onChange(obj);
  };
  const addRow = () => onChange({ ...(value && typeof value === 'object' ? value : {}), '': '' } as any);
  const removeRow = (i: number) => {
    const next = rows.filter((_, idx) => idx !== i);
    const obj: Record<string, string> = {};
    for (const r of next) if (r.name.trim()) obj[r.name.trim()] = r.val;
    onChange(obj);
  };

  return (
    <div className="space-y-1.5">
      {rows.length === 0 && (
        <div className="text-[10px] text-muted-foreground">Параметров нет</div>
      )}
      {rows.map((r, i) => (
        <div key={i} className="flex items-center gap-1 text-[10px]">
          <Input
            className="h-7 min-w-0 flex-1 text-[10px]"
            value={r.name}
            placeholder="имя параметра"
            onChange={(e) => setRow(i, { name: e.target.value })}
          />
          <span className="text-muted-foreground">=</span>
          <Input
            className="h-7 min-w-0 flex-1 text-[10px]"
            value={r.val}
            placeholder="значение"
            onChange={(e) => setRow(i, { val: e.target.value })}
          />
          <button className="p-0.5 text-muted-foreground hover:text-destructive" onClick={() => removeRow(i)} title="Убрать параметр">
            <X className="h-3 w-3" />
          </button>
        </div>
      ))}
      <div className="flex items-center justify-between gap-2">
        <Button size="sm" variant="outline" className="h-6 text-[10px] px-2" onClick={addRow}>
          <Plus className="h-3 w-3 mr-1" /> Параметр
        </Button>
        <span className="text-[9px] text-muted-foreground">в отборах: @param.имя</span>
      </div>
    </div>
  );
}
